import {FC, useEffect} from 'react';
import {useNavigate, useParams, useLocation} from 'react-router';
import {useDispatch, useSelector} from 'react-redux';
import Text from '@components/atoms/Text';
import {
  EditSystemPlanContentBlock,
  EditSystemPlanPageWrapper,
  EditSystemPlanTitleBlock,
  EditSystemPlanActions,
} from './EditSystemPlanPage.styles';
import {formatNonTodayFull} from '@utils/common';
import {getSystems, getSystemSnapshot, systems} from '@store/slices/systems';
import {
  addPlan,
  getPlans,
  selectCoveragePlans,
  getPlan as getPlanThunk,
  selectCurrentPlan,
  addTemporaryPlanCell,
  editPlanCells,
  setCurrentPlan,
} from '@store/slices/coveragePlans';
import {getTests} from '@store/slices/tests';
import {IPlanCell, IPlanModule, IPlanToSave, ISystem} from '@types';
import Button from '@components/atoms/Button';
import {ERoutes} from '@constants/routes';
import SubsystemEditPlanBlock from '@organisms/SubsystemEditPlanBlock';
import {IDLE} from '@constants/loadingState';
import {AppDispatch, RootState} from '@store';

const EditSystemPlanPage: FC = () => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const dateStr = params.get('date');
  const dateText = (() => {
    if (!dateStr) return '';
    const m = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!m) return '';
    const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    d.setHours(0, 0, 0, 0);
    return formatNonTodayFull(d).replace(',', '');
  })();
  const readOnly = params.get('readonly') === '1';

  const {id} = useParams<{id: string}>();
  const {loading, addLoading, editLoading, deleteLoading} = useSelector(
    (state: RootState) => state.systems,
  );
  const systemsArr: ISystem[] = useSelector(systems);
  const currentSystem = systemsArr.find(system => String(system.id) === id);
  const plans = useSelector(selectCoveragePlans);
  const currentPlan = useSelector(selectCurrentPlan);

  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();

  useEffect(() => {
    dispatch(getSystems({}));
    dispatch(getTests({}));
    dispatch(getPlans(Number(id)));
  }, []);

  useEffect(() => {
    if (
      loading === IDLE &&
      addLoading === IDLE &&
      editLoading === IDLE &&
      deleteLoading === IDLE
    ) {
      dispatch(getSystemSnapshot({system_id: Number(id)}));
    }
  }, [loading, addLoading, editLoading, deleteLoading]);

  useEffect(() => {
    if (!id || !dateStr) return;
    const plan = plans.find(p => p.date === dateStr);
    if (plan) {
      dispatch(getPlanThunk({plan_id: plan.id, system_id: Number(id)}));
    } else if (currentSystem?.snapshot) {
      dispatch(setCurrentPlan(currentSystem.snapshot));
    }
  }, [id, dateStr, plans, currentSystem?.snapshot]);

  const onAddCell = (cellData: {
    coverage: number;
    auto_coverage: number;
    module_id: number;
    subsystem_id: number;
    test_type_id: number;
  }) => {
    dispatch(
      addTemporaryPlanCell({
        coverage: cellData.coverage,
        auto_coverage: cellData.auto_coverage,
        module_id: cellData.module_id,
        subsystem_id: cellData.subsystem_id,
        test_type_id: cellData.test_type_id,
      }),
    );
  };

  const onSave = () => {
    if (!currentSystem || !dateStr) return;
    const existingPlan = plans.find(p => p.date === dateStr);
    if (existingPlan) {
      dispatch(editPlanCells({plan_id: existingPlan.id}));
    } else {
      const planToSave: IPlanToSave = {date: dateStr};
      planToSave.subsystems = currentSystem?.snapshot?.subsystems.map(subsystem => {
        const modules: IPlanModule[] = [];
        subsystem.modules.forEach(module => {
          const cells: IPlanCell[] = [];
          module.cells?.forEach(cell => {
            cells.push({
              coverage: cell.coverage || 0,
              auto_coverage: cell.auto_coverage || 0,
              test_type_id: cell.test_type_id,
            });
          });
          modules.push({
            module_id: module.id,
            cells: cells,
          });
        });
        return {subsystem_id: subsystem.id, modules: modules};
      });

      dispatch(
        addPlan({
          systemId: Number(currentSystem.id),
          planData: planToSave,
        }),
      );
    }
    navigate(ERoutes.SYSTEM_DETAILS.replace(':id', String(id)));
  };

  return (
    <EditSystemPlanPageWrapper>
      <EditSystemPlanTitleBlock>
        <Text text={currentSystem?.name || 'Система'} type="headline" />
        <Text
          text={dateText ? `План покрытия системы на ${dateText}` : ''}
          type="captionB"
        />
      </EditSystemPlanTitleBlock>
      <EditSystemPlanContentBlock>
        {currentPlan?.subsystems.map(subsystem => {
          return (
            <SubsystemEditPlanBlock
              key={`subsystem-edit-plan-block-${subsystem.id}`}
              subsystem={subsystem}
              addCellForPlan={onAddCell}
              readOnly={readOnly}
            />
          );
        })}
      </EditSystemPlanContentBlock>

      {!readOnly && (
        <EditSystemPlanActions>
          <Button title="Сохранить план" variant="filled" onClick={onSave} />

          <Button
            title="Отменить"
            onClick={() => navigate(ERoutes.SYSTEM_DETAILS.replace(':id', String(id)))}
          />
        </EditSystemPlanActions>
      )}
    </EditSystemPlanPageWrapper>
  );
};

export default EditSystemPlanPage;
